import { useCallback, useEffect } from 'react';
import { Flex, RemoteImage } from '@banhala/ui-kit';
import { AD_RESPONSE_TYPE, RETAIL_AD_SLOT_ID } from 'shared/src/api';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useRetailAd } from '../queries/useRetailAd';
import { useLogRetailAd } from '../hooks/useLogRetailAd';

const HORIZONTAL_PADDING = 16;

interface RetailAdBannerProps {
  contentWidth?: number | null;
}

export const RetailAdBanner = ({ contentWidth }: RetailAdBannerProps) => {
  const { data: retailAd } = useRetailAd(RETAIL_AD_SLOT_ID.OVERVIEW_TOP_BANNER);
  const { openDeeplink } = useOpenDeeplink();
  const { logDisplayAds, logClickAds } = useLogRetailAd(
    RETAIL_AD_SLOT_ID.OVERVIEW_TOP_BANNER,
  );

  const handlePress = useCallback(() => {
    if (retailAd?.ad_info?.creative_link == null) return;
    logClickAds();
    openDeeplink(retailAd.ad_info.creative_link);
  }, [retailAd, openDeeplink, logClickAds]);

  const isVisible =
    retailAd != null &&
    retailAd.ad_response_type === AD_RESPONSE_TYPE.OK &&
    retailAd.ad_info != null &&
    contentWidth != null;

  if (!isVisible || retailAd?.ad_info == null) return null;

  const adInfo = retailAd.ad_info;
  const bannerWidth = contentWidth - HORIZONTAL_PADDING * 2;
  const bannerHeight = adInfo.creative_media_ratio
    ? bannerWidth / adInfo.creative_media_ratio
    : bannerWidth * (72 / 222);

  return (
    <Flex
      padding={{ horizontal: HORIZONTAL_PADDING, vertical: 8 }}
      onPress={handlePress}
    >
      <RetailAdBannerImage
        key={adInfo.ad_executable_id}
        uri={adInfo.creative_media_link}
        width={bannerWidth}
        height={bannerHeight}
        onMount={logDisplayAds}
      />
    </Flex>
  );
};

/**
 * 별도 컴포넌트로 분리해서 mount 시점을 DISPLAY 시그널로 사용.
 * - 부모(`RetailAdBanner`)가 isVisible + ad_info 조건을 만족한 이후에만 이 컴포넌트가
 *   mount 되므로, mount = "이미지를 화면에 그릴 준비가 된 시점" 으로 해석 가능.
 * - 광고가 바뀌면 부모에서 ad_executable_id 를 key 로 주어 강제 remount 되도록 함.
 *   → 같은 광고는 1회, 광고 교체 시 새로 emit (native 의 동작과 일치).
 */
const RetailAdBannerImage = ({
  uri,
  width,
  height,
  onMount,
}: {
  uri: string;
  width: number;
  height: number;
  onMount: () => void;
}) => {
  useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <Flex borderRadius={4} overflow="hidden">
      <RemoteImage uri={uri} width={width} height={height} />
    </Flex>
  );
};
